import { c as _c } from "react/compiler-runtime";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { InviteMemberButton } from "@components/common/invite-member-button";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import { EnableGroupCreationCheckbox } from "@components/groups/enable-group-creation-checkbox";
import { CompanyRoleAutoComplete } from "@components/role/role-autocomplete/company-role-autocomplete";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { Stack } from "@mui-wrapper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { INVITE_DIALOG_CLOSE_DELAY_MS } from "@src/constants/invite-constants";
import { setMany } from "@store/members/members-slice";
import { useAppDispatch } from "@store/store-helper";
import { delay } from "@utils/time-utils";
import { getAssignableWorkspaceRoles } from "@utils/workspace-role-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { createGroupsPermission } from "@utils/user-utils";
import { useState } from "react";
import { Dialog } from "@flat-ui";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { useCompanyMembers } from "@hooks/use-company-members";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";
/** Renders the invite member button and the functionality in member page */
export function InviteMemberToCompany(t0) {
  const $ = _c(67);
  const {
    buttonVariant: t1
  } = t0;
  const buttonVariant = t1 === undefined ? "outlined" : t1;
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    respondToUser
  } = useRespondToInvite();
  const {
    trackEvent
  } = useTrackEvent();
  const companyMembers = useCompanyMembers();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const defaultRole = "MEMBER";
  const [selectedRole, setSelectedRole] = useState(defaultRole);
  const [selectedGroupId, setSelectedGroupId] = useState(undefined);
  const [shouldCreateGroups, setShouldCreateGroups] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== companyMembers) {
    t3 = companyMembers.map(_temp);
    $[1] = companyMembers;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== companyMembers || $[4] !== t3) {
    t4 = {
      entityName: "company",
      existingMembers: companyMembers,
      options: t2,
      companyMemberIds: t3,
      invalidInputMessage: "Enter a valid email address"
    };
    $[3] = companyMembers;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    selectedMembers,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset
  } = useMembersAutocomplete(t4);
  const shouldShowGroupList = selectedRole === "COMPANY_MANAGER" || selectedRole === "GROUP_VIEWER" || selectedRole === "PROJECT_MANAGER";
  let t5;
  if ($[6] !== isMemberInputValid || $[7] !== selectedGroupId || $[8] !== shouldShowGroupList || $[9] !== uniqueSelectedValues) {
    t5 = shouldDisableSendInvite(uniqueSelectedValues, isMemberInputValid) || shouldShowGroupList && !selectedGroupId;
    $[6] = isMemberInputValid;
    $[7] = selectedGroupId;
    $[8] = shouldShowGroupList;
    $[9] = uniqueSelectedValues;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const shouldDisableConfirm = t5;
  let t6;
  if ($[11] !== reset) {
    t6 = function onOpenInviteDialog() {
      reset();
      setIsDialogOpen(true);
      setSelectedRole("MEMBER");
      setSelectedGroupId(undefined);
    };
    $[11] = reset;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const onOpenInviteDialog = t6;
  let createPayloadAndSendInvite;
  if ($[13] !== dispatch || $[14] !== respondToUser || $[15] !== selectedGroupId || $[16] !== selectedRole || $[17] !== shouldCreateGroups || $[18] !== uniqueSelectedValues) {
    createPayloadAndSendInvite = async function createPayloadAndSendInvite(t7) {
      const {
        shouldAddMemberToGroup,
        companyId: companyId_0
      } = t7;
      const permission = shouldCreateGroups && selectedRole === "COMPANY_MANAGER" ? createGroupsPermission : undefined;
      if (shouldAddMemberToGroup && selectedGroupId) {
        const payload = {
          assignments: [{
            identities: uniqueSelectedValues,
            role: selectedRole,
            permission
          }]
        };
        const response = await faroCoreApiClient.groups.addMembersToGroup({
          companyId: companyId_0,
          groupId: selectedGroupId,
          payload
        });
        respond(response);
      } else {
        const payload_0 = {
          assignments: [{
            identities: uniqueSelectedValues,
            role: selectedRole,
            permission
          }]
        };
        const response_0 = await faroCoreApiClient.company.addMembersToCompany({
          companyId: companyId_0,
          payload: payload_0
        });
        respond(response_0);
      }
    };
    function respond(response_1) {
      if (response_1.data.members) {
        dispatch(setMany(response_1.data.members.filter(_temp2)));
      }
      respondToUser(response_1, uniqueSelectedValues.length);
    }
    $[13] = dispatch;
    $[14] = respondToUser;
    $[15] = selectedGroupId;
    $[16] = selectedRole;
    $[17] = shouldCreateGroups;
    $[18] = uniqueSelectedValues;
    $[19] = createPayloadAndSendInvite;
  } else {
    createPayloadAndSendInvite = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = async function closeDialog() {
      setIsDialogOpen(false);
      await delay(INVITE_DIALOG_CLOSE_DELAY_MS);
      setIsLoading(false);
    };
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const closeDialog = t7;
  let t8;
  if ($[21] !== companyId || $[22] !== createPayloadAndSendInvite || $[23] !== handleErrorWithToast || $[24] !== selectedGroupId || $[25] !== selectedRole || $[26] !== trackEvent || $[27] !== uniqueSelectedValues.length) {
    t8 = async function handleConfirm() {
      setIsLoading(true);
      trackEvent({
        name: WorkspaceEvents.inviteMembers,
        props: {
          numberOfMembers: uniqueSelectedValues.length,
          role: selectedRole
        }
      });
      ;
      try {
        await createPayloadAndSendInvite({
          shouldAddMemberToGroup: !!selectedGroupId,
          companyId
        });
        closeDialog();
      } catch (t9) {
        const error = t9;
        setIsLoading(false);
        handleErrorWithToast({
          id: `inviteMember-${Date.now().toString()}`,
          title: "Cannot invite member(s)",
          error
        });
      }
    };
    $[21] = companyId;
    $[22] = createPayloadAndSendInvite;
    $[23] = handleErrorWithToast;
    $[24] = selectedGroupId;
    $[25] = selectedRole;
    $[26] = trackEvent;
    $[27] = uniqueSelectedValues.length;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const handleConfirm = t8;
  let t9;
  if ($[29] !== buttonVariant || $[30] !== onOpenInviteDialog) {
    t9 = <InviteMemberButton buttonVariant={buttonVariant} onClick={onOpenInviteDialog} />;
    $[29] = buttonVariant;
    $[30] = onOpenInviteDialog;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const t10 = canAssignManagers ? "Invite members to workspace" : "Invite viewers to workspace";
  let t11;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      marginBottom: SPACE_ELEMENTS_OF_MODAL
    };
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = role => role && setSelectedRole(role);
    t13 = () => setSelectedRole(defaultRole);
    $[33] = t12;
    $[34] = t13;
  } else {
    t12 = $[33];
    t13 = $[34];
  }
  let t14;
  if ($[35] !== canAssignManagers) {
    t14 = getAssignableWorkspaceRoles("invite", canAssignManagers);
    $[35] = canAssignManagers;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== selectedRole || $[38] !== t14) {
    t15 = <Stack sx={t11}><CompanyRoleAutoComplete selectedRole={selectedRole} shouldAllowAllRolesSelection={true} onChange={t12} onResetSelection={t13} roles={t14} isRequired={true} /></Stack>;
    $[37] = selectedRole;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== selectedGroupId || $[41] !== shouldShowGroupList) {
    t16 = shouldShowGroupList && <Stack sx={{
      marginBottom: SPACE_ELEMENTS_OF_MODAL
    }}><LabelWithHelp title="Group" isRequired={true} /><GroupAutoComplete callbackOnGroupSelected={setSelectedGroupId} selectedGroupId={selectedGroupId} placeholder="Please select a group..." /></Stack>;
    $[40] = selectedGroupId;
    $[41] = shouldShowGroupList;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = [];
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== handleMemberSelect || $[45] !== helperText || $[46] !== isNewEmailValid || $[47] !== onInputChange || $[48] !== selectedMembers) {
    t18 = <MembersAutocomplete isRequired={true} hasAutoFocus={true} options={t17} label="Emails" helperText={helperText} selectedOptions={selectedMembers} isValid={isNewEmailValid} onInputChange={onInputChange} handleMemberSelect={handleMemberSelect} placeHolder="Enter email addresses (Separated by comma, semicolon, or space)" />;
    $[44] = handleMemberSelect;
    $[45] = helperText;
    $[46] = isNewEmailValid;
    $[47] = onInputChange;
    $[48] = selectedMembers;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== selectedMembers.length || $[51] !== selectedRole || $[52] !== shouldCreateGroups) {
    t19 = <EnableGroupCreationCheckbox selectedRole={selectedRole} shouldCreateGroups={shouldCreateGroups} membersCount={selectedMembers.length} setShouldCreateGroups={setShouldCreateGroups} />;
    $[50] = selectedMembers.length;
    $[51] = selectedRole;
    $[52] = shouldCreateGroups;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== handleConfirm || $[55] !== isDialogOpen || $[56] !== isLoading || $[57] !== shouldDisableConfirm || $[58] !== t10 || $[59] !== t15 || $[60] !== t16 || $[61] !== t18 || $[62] !== t19) {
    t20 = <Dialog title={t10} confirmText="Send Invite" open={isDialogOpen} onConfirm={handleConfirm} showSpinner={isLoading} isConfirmDisabled={shouldDisableConfirm} onCancel={closeDialog} dataTestId="invite-members-to-workspace-dialog">{t15}{t16}{t18}{t19}</Dialog>;
    $[54] = handleConfirm;
    $[55] = isDialogOpen;
    $[56] = isLoading;
    $[57] = shouldDisableConfirm;
    $[58] = t10;
    $[59] = t15;
    $[60] = t16;
    $[61] = t18;
    $[62] = t19;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== t20 || $[65] !== t9) {
    t21 = <>{t9}{t20}</>;
    $[64] = t20;
    $[65] = t9;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  return t21;
}
function _temp2(member_0) {
  return member_0.kind === "user";
}
function _temp(member) {
  return member.identity;
}